// =============================================================================
// BUDDY AUDIT LOG — chronological moderation event trail (legal-grade)
//
// Two callers:
//   • Tenant view  — <BuddyAuditLogCard tenantId tenantName />
//       Shows events for a single tenant. Always renders (with an
//       empty-state row when no events) so the card doesn't flicker
//       in/out across loads.
//
//   • Super-admin  — <BuddyAuditLogSuperAdminCard />
//       Cross-tenant view with a "Tenant" dropdown filter that resolves
//       names from the platform store. Adds a Tenant column to both
//       the on-screen table and the CSV / PDF exports.
//
// Both share the same date-range filter, count chips, and the two
// export buttons (CSV + print-as-PDF).
// =============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ShieldAlert, ShieldCheck, Download, FileText, RefreshCw, Calendar, Building2 } from 'lucide-react';
import { usePlatformStore } from '@/lib/store';

const APP_SUPABASE_URL = import.meta.env.VITE_APP_SUPABASE_URL || '';
const PROVISION_SECRET = import.meta.env.VITE_PROVISION_SECRET || '';

interface AuditEvent {
  /** Present on cross-tenant rows; undefined for single-tenant view. */
  tenant_id?:             string;
  event_at:               string;
  event_type:             'block' | 'unblock';
  user_id:                string;
  user_email:             string | null;
  user_full_name:         string | null;
  strike_count_at_block:  number;
  matched_terms:          string[] | null;
  excerpt:                string | null;
  acted_by_email:         string | null;
  acted_by_role:          string | null;
  block_history_id:       string;
}

// ─── Single-tenant card ─────────────────────────────────────────────
export function BuddyAuditLogCard({
  tenantId,
  tenantName,
}: {
  tenantId: string;
  tenantName: string;
}) {
  const events = useAuditEvents({ tenantId });
  return (
    <AuditLogPanel
      title="Buddy Trips audit log"
      tenantName={tenantName}
      includeTenantColumn={false}
      tenantNamesById={new Map([[tenantId, tenantName]])}
      tenantFilterOptions={null}
      {...events}
    />
  );
}

// ─── Cross-tenant super-admin card ──────────────────────────────────
export function BuddyAuditLogSuperAdminCard() {
  const tenants = usePlatformStore(s => s.tenants);
  const tenantNamesById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tenants) m.set(t.id, t.name || t.slug || t.id.slice(0, 8));
    return m;
  }, [tenants]);

  const [tenantFilter, setTenantFilter] = useState<string>('');
  const events = useAuditEvents({ allTenants: true, tenantId: tenantFilter || undefined });

  // Show options only for tenants that actually have audit events plus
  // the currently-selected tenant — keeps the dropdown short and
  // relevant.
  const tenantFilterOptions = useMemo(() => {
    const seen = new Set<string>();
    if (tenantFilter) seen.add(tenantFilter);
    for (const ev of events.events ?? []) if (ev.tenant_id) seen.add(ev.tenant_id);
    return Array.from(seen).map(id => ({ id, name: tenantNamesById.get(id) || id.slice(0, 8) + '…' }));
  }, [events.events, tenantFilter, tenantNamesById]);

  return (
    <AuditLogPanel
      title="Buddy Trips audit log — across tenants"
      tenantName={tenantFilter ? (tenantNamesById.get(tenantFilter) ?? 'Selected tenant') : 'All tenants'}
      includeTenantColumn
      tenantNamesById={tenantNamesById}
      tenantFilterOptions={tenantFilterOptions}
      tenantFilter={tenantFilter}
      onTenantFilterChange={setTenantFilter}
      {...events}
    />
  );
}

// ─── Shared fetch hook ──────────────────────────────────────────────
function useAuditEvents(opts: {
  tenantId?: string;
  allTenants?: boolean;
}) {
  const [events, setEvents]    = useState<AuditEvent[] | null>(null);
  const [loading, setLoading]  = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [from, setFrom]        = useState<string>('');
  const [to,   setTo]          = useState<string>('');

  const load = useCallback(async () => {
    if (!APP_SUPABASE_URL || !PROVISION_SECRET) { setLoading(false); return; }
    setRefreshing(true);
    try {
      const body: any = {
        mode: opts.allTenants ? 'audit_trail_all' : 'audit_trail',
      };
      if (!opts.allTenants && opts.tenantId) body.tenant_id = opts.tenantId;
      if (opts.allTenants  && opts.tenantId) body.tenant_id = opts.tenantId;
      if (from) body.from = new Date(from + 'T00:00:00').toISOString();
      if (to)   body.to   = new Date(to   + 'T23:59:59').toISOString();
      const res = await fetch(`${APP_SUPABASE_URL}/functions/v1/admin-buddy-analytics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-provision-secret': PROVISION_SECRET },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (json.ok) setEvents(json.data ?? []);
      else         setEvents([]);
    } catch (err) {
      console.warn('[BuddyAuditLogCard] failed:', err);
      setEvents([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [opts.allTenants, opts.tenantId, from, to]);

  useEffect(() => { load(); }, [load]);

  return { events, loading, refreshing, from, setFrom, to, setTo, reload: load };
}

// ─── Render panel ───────────────────────────────────────────────────
function AuditLogPanel({
  title,
  tenantName,
  includeTenantColumn,
  tenantNamesById,
  tenantFilterOptions,
  tenantFilter,
  onTenantFilterChange,
  events,
  loading,
  refreshing,
  from, setFrom,
  to,   setTo,
  reload,
}: {
  title: string;
  tenantName: string;
  includeTenantColumn: boolean;
  tenantNamesById: Map<string, string>;
  tenantFilterOptions: Array<{ id: string; name: string }> | null;
  tenantFilter?: string;
  onTenantFilterChange?: (id: string) => void;
  events: AuditEvent[] | null;
  loading: boolean;
  refreshing: boolean;
  from: string; setFrom: (v: string) => void;
  to:   string; setTo:   (v: string) => void;
  reload: () => void;
}) {
  const stats = useMemo(() => {
    if (!events) return { blocks: 0, unblocks: 0 };
    let b = 0, u = 0;
    for (const e of events) (e.event_type === 'block' ? b++ : u++);
    return { blocks: b, unblocks: u };
  }, [events]);

  const handleExportCsv = () => {
    if (!events || events.length === 0) return;
    const csv = toCsv(events, includeTenantColumn, tenantNamesById);
    downloadBlob(csv, csvFilename(tenantName, from, to), 'text/csv;charset=utf-8;');
  };
  const handleExportPdf = () => {
    if (!events || events.length === 0) return;
    openPrintableReport(events, tenantName, from, to, includeTenantColumn, tenantNamesById);
  };

  return (
    <div className="rounded-xl border border-surface-200 bg-surface-0 p-5">
      <div className="flex items-start gap-2 mb-3 flex-wrap">
        <ShieldAlert size={16} className="text-amber-600 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-surface-900">{title}</h3>
          <p className="text-[11px] text-surface-500 mt-0.5">
            Block / unblock events with timestamp, affected user, and acting admin. Exportable for legal review.
          </p>
        </div>
        <button
          onClick={reload}
          disabled={refreshing}
          className="p-1.5 rounded-md hover:bg-surface-100 text-surface-500"
          title="Refresh"
        >
          <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Filters + export buttons */}
      <div className="flex flex-wrap items-end gap-2 mb-4">
        {tenantFilterOptions && (
          <div className="flex flex-col gap-0.5">
            <label className="text-[10px] uppercase tracking-wider text-surface-500 flex items-center gap-1">
              <Building2 size={10} /> Tenant
            </label>
            <select
              value={tenantFilter ?? ''}
              onChange={e => onTenantFilterChange?.(e.target.value)}
              className="text-xs px-2 py-1 rounded-md border border-surface-200 bg-surface-0 text-surface-900 min-w-[160px]"
            >
              <option value="">All tenants</option>
              {tenantFilterOptions.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="flex flex-col gap-0.5">
          <label className="text-[10px] uppercase tracking-wider text-surface-500 flex items-center gap-1">
            <Calendar size={10} /> From
          </label>
          <input
            type="date"
            value={from}
            onChange={e => setFrom(e.target.value)}
            className="text-xs px-2 py-1 rounded-md border border-surface-200 bg-surface-0 text-surface-900"
          />
        </div>
        <div className="flex flex-col gap-0.5">
          <label className="text-[10px] uppercase tracking-wider text-surface-500 flex items-center gap-1">
            <Calendar size={10} /> To
          </label>
          <input
            type="date"
            value={to}
            onChange={e => setTo(e.target.value)}
            className="text-xs px-2 py-1 rounded-md border border-surface-200 bg-surface-0 text-surface-900"
          />
        </div>
        {(from || to) && (
          <button
            onClick={() => { setFrom(''); setTo(''); }}
            className="text-[11px] text-surface-500 underline px-2 py-1.5"
          >
            Clear
          </button>
        )}
        <div className="flex-1" />
        <button
          onClick={handleExportCsv}
          disabled={!events || events.length === 0}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold bg-surface-100 hover:bg-surface-200 text-surface-700 disabled:opacity-40 transition-colors"
          title="Download as CSV (opens in Excel)"
        >
          <Download size={12} />
          CSV
        </button>
        <button
          onClick={handleExportPdf}
          disabled={!events || events.length === 0}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold bg-brand-600 hover:bg-brand-700 text-white disabled:opacity-40 transition-colors"
          title="Open print-ready report → save as PDF"
        >
          <FileText size={12} />
          PDF
        </button>
      </div>

      {/* Counts */}
      <div className="flex flex-wrap items-center gap-3 mb-3 text-[11px] text-surface-500">
        <span className="inline-flex items-center gap-1">
          <ShieldAlert size={11} className="text-red-600" />
          <strong className="text-surface-900 tabular-nums">{stats.blocks}</strong> block events
        </span>
        <span className="inline-flex items-center gap-1">
          <ShieldCheck size={11} className="text-emerald-600" />
          <strong className="text-surface-900 tabular-nums">{stats.unblocks}</strong> unblock events
        </span>
      </div>

      {/* Event table — always rendered (no auto-hide flicker). Empty
          state shown inline when no events match. */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <RefreshCw size={16} className="animate-spin text-surface-400" />
        </div>
      ) : !events || events.length === 0 ? (
        <p className="text-[11px] text-surface-500 italic py-4 text-center">
          No moderation events {from || to ? 'in the selected range' : 'yet'}.
        </p>
      ) : (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-left text-[11px]">
            <thead className="text-[10px] uppercase tracking-wider text-surface-500">
              <tr className="border-b border-surface-200">
                <th className="py-2 px-2 font-semibold">When</th>
                <th className="py-2 px-2 font-semibold">Event</th>
                {includeTenantColumn && (
                  <th className="py-2 px-2 font-semibold">Tenant</th>
                )}
                <th className="py-2 px-2 font-semibold">User</th>
                <th className="py-2 px-2 font-semibold">Trigger / Acted by</th>
              </tr>
            </thead>
            <tbody>
              {events.map(ev => (
                <tr
                  key={`${ev.block_history_id}-${ev.event_type}`}
                  className="border-b border-surface-100 hover:bg-surface-50/50"
                >
                  <td className="py-2 px-2 text-surface-700 tabular-nums whitespace-nowrap">
                    {formatDateTime(ev.event_at)}
                  </td>
                  <td className="py-2 px-2">
                    {ev.event_type === 'block' ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
                        <ShieldAlert size={10} /> BLOCK
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                        <ShieldCheck size={10} /> UNBLOCK
                      </span>
                    )}
                  </td>
                  {includeTenantColumn && (
                    <td className="py-2 px-2 max-w-[160px]">
                      <p className="font-semibold text-surface-900 truncate">
                        {ev.tenant_id ? tenantNamesById.get(ev.tenant_id) ?? ev.tenant_id.slice(0, 8) + '…' : '—'}
                      </p>
                    </td>
                  )}
                  <td className="py-2 px-2 max-w-[200px]">
                    <p className="font-semibold text-surface-900 truncate">
                      {ev.user_full_name || ev.user_email?.split('@')[0] || 'Unknown'}
                    </p>
                    {ev.user_email && (
                      <p className="text-[10px] text-surface-500 truncate">{ev.user_email}</p>
                    )}
                  </td>
                  <td className="py-2 px-2 max-w-[300px]">
                    {ev.event_type === 'block' ? (
                      <>
                        <p className="text-amber-700 dark:text-amber-400">
                          <span className="text-surface-500">System auto-block at </span>
                          <strong>{ev.strike_count_at_block} strikes</strong>
                        </p>
                        {ev.matched_terms && ev.matched_terms.length > 0 && (
                          <p className="text-amber-700 dark:text-amber-400 font-mono text-[10px] truncate">
                            terms: {ev.matched_terms.slice(0, 4).join(', ')}
                          </p>
                        )}
                        {ev.excerpt && (
                          <p className="text-surface-500 italic truncate">"{ev.excerpt}"</p>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-surface-700 truncate">
                          {ev.acted_by_email
                            ? <>by <strong>{ev.acted_by_email}</strong></>
                            : <span className="text-surface-400 italic">acting admin not recorded</span>}
                        </p>
                        {ev.acted_by_role && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-surface-100 text-surface-600 mt-0.5">
                            {ev.acted_by_role.replace('_', ' ')}
                          </span>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Export helpers ────────────────────────────────────────────────

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: '2-digit', second: '2-digit',
  });
}

function toCsv(
  events: AuditEvent[],
  includeTenantColumn: boolean,
  tenantNamesById: Map<string, string>,
): string {
  const headers = [
    'event_at_iso',
    'event_at_local',
    'event_type',
    ...(includeTenantColumn ? ['tenant_id', 'tenant_name'] : []),
    'user_id',
    'user_email',
    'user_full_name',
    'strike_count_at_block',
    'matched_terms',
    'excerpt',
    'acted_by_email',
    'acted_by_role',
    'block_history_id',
  ];
  const lines = [headers.join(',')];
  for (const e of events) {
    const tenantId = e.tenant_id ?? '';
    const tenantName = e.tenant_id ? (tenantNamesById.get(e.tenant_id) ?? '') : '';
    const tenantCols = includeTenantColumn ? [tenantId, tenantName] : [];
    lines.push([
      e.event_at,
      formatDateTime(e.event_at),
      e.event_type,
      ...tenantCols,
      e.user_id,
      e.user_email ?? '',
      e.user_full_name ?? '',
      e.event_type === 'block' ? String(e.strike_count_at_block ?? '') : '',
      e.event_type === 'block' ? (e.matched_terms ?? []).join('; ') : '',
      e.event_type === 'block' ? (e.excerpt ?? '') : '',
      e.acted_by_email ?? '',
      e.acted_by_role  ?? '',
      e.block_history_id,
    ].map(csvEscape).join(','));
  }
  return lines.join('\n');
}

function csvEscape(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadBlob(content: string, filename: string, mime: string): void {
  const blob = new Blob(['﻿' + content], { type: mime });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function csvFilename(tenant: string, from: string, to: string): string {
  const slug = tenant.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'audit';
  const range = from || to ? `_${from || 'start'}_to_${to || 'now'}` : '';
  return `buddy-audit_${slug}${range}_${new Date().toISOString().slice(0, 10)}.csv`;
}

function openPrintableReport(
  events: AuditEvent[],
  tenant: string,
  from: string, to: string,
  includeTenantColumn: boolean,
  tenantNamesById: Map<string, string>,
): void {
  const win = window.open('', '_blank');
  if (!win) {
    alert('Pop-up blocked — allow pop-ups for this site to export the PDF report.');
    return;
  }
  const range = from || to ? `${from || 'start'} → ${to || 'now'}` : 'All time';
  const generated = new Date().toLocaleString();
  const rows = events.map(e => {
    const tenantCell = includeTenantColumn
      ? `<td><div class="user">${escapeHtml(e.tenant_id ? (tenantNamesById.get(e.tenant_id) ?? e.tenant_id.slice(0, 8) + '…') : '—')}</div></td>`
      : '';
    const trigger = e.event_type === 'block'
      ? `<div>System auto-block @ <b>${e.strike_count_at_block ?? '—'} strikes</b></div>`
        + (e.matched_terms && e.matched_terms.length > 0
            ? `<div class="mono">terms: ${escapeHtml(e.matched_terms.join(', '))}</div>`
            : '')
        + (e.excerpt ? `<div class="excerpt">"${escapeHtml(e.excerpt)}"</div>` : '')
      : `<div>by <b>${escapeHtml(e.acted_by_email ?? 'not recorded')}</b></div>`
        + (e.acted_by_role ? `<div class="role">${escapeHtml(e.acted_by_role.replace('_', ' '))}</div>` : '');
    return `
      <tr>
        <td class="ts">${escapeHtml(formatDateTime(e.event_at))}</td>
        <td><span class="ev ev-${e.event_type}">${e.event_type === 'block' ? 'BLOCK' : 'UNBLOCK'}</span></td>
        ${tenantCell}
        <td>
          <div class="user">${escapeHtml(e.user_full_name ?? e.user_email?.split('@')[0] ?? 'Unknown')}</div>
          <div class="email">${escapeHtml(e.user_email ?? '')}</div>
        </td>
        <td>${trigger}</td>
      </tr>`;
  }).join('');

  const tenantCol = includeTenantColumn
    ? '<th style="width: 14%;">Tenant</th>'
    : '';

  win.document.open();
  win.document.write(`<!doctype html>
<html><head><meta charset="utf-8">
<title>Buddy Trips audit log — ${escapeHtml(tenant)}</title>
<style>
  @page { size: A4 landscape; margin: 14mm; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #1f2937; font-size: 12px; }
  h1 { font-size: 18px; margin: 0 0 4px; }
  .meta { font-size: 11px; color: #6b7280; margin-bottom: 14px; }
  .meta strong { color: #1f2937; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border-bottom: 1px solid #e5e7eb; padding: 7px 8px; vertical-align: top; text-align: left; }
  th { background: #f9fafb; text-transform: uppercase; font-size: 10px; letter-spacing: 0.05em; color: #6b7280; }
  td.ts { white-space: nowrap; font-variant-numeric: tabular-nums; color: #374151; }
  .ev { display: inline-block; padding: 2px 6px; border-radius: 4px; font-size: 9px; font-weight: 700; letter-spacing: 0.05em; }
  .ev-block { background: #fee2e2; color: #b91c1c; }
  .ev-unblock { background: #d1fae5; color: #047857; }
  .user { font-weight: 600; }
  .email { color: #6b7280; font-size: 10px; }
  .mono { font-family: ui-monospace, monospace; color: #b45309; font-size: 10px; margin-top: 1px; }
  .excerpt { color: #6b7280; font-style: italic; font-size: 10px; margin-top: 1px; }
  .role { display: inline-block; padding: 1px 5px; border-radius: 3px; background: #f3f4f6; color: #374151; font-size: 9px; text-transform: uppercase; letter-spacing: 0.04em; margin-top: 2px; }
  .footer { margin-top: 18px; font-size: 9px; color: #9ca3af; }
  @media print { .no-print { display: none; } }
  .actions { margin-bottom: 12px; }
  .actions button { padding: 6px 12px; border: 1px solid #d1d5db; border-radius: 6px; background: #fff; font-size: 12px; cursor: pointer; margin-right: 6px; }
</style>
</head><body>
  <div class="actions no-print">
    <button onclick="window.print()">Save as PDF / Print</button>
    <button onclick="window.close()">Close</button>
  </div>
  <h1>Buddy Trips audit log</h1>
  <div class="meta">
    <strong>${includeTenantColumn ? 'Scope' : 'Tenant'}:</strong> ${escapeHtml(tenant)} ·
    <strong>Range:</strong> ${escapeHtml(range)} ·
    <strong>Events:</strong> ${events.length} ·
    <strong>Generated:</strong> ${escapeHtml(generated)}
  </div>
  <table>
    <thead>
      <tr>
        <th style="width: ${includeTenantColumn ? '14%' : '18%'};">When</th>
        <th style="width: 8%;">Event</th>
        ${tenantCol}
        <th style="width: ${includeTenantColumn ? '22%' : '26%'};">User</th>
        <th>Trigger / Acted by</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="footer">
    This report is a verbatim export of moderation events recorded in the Buddy Trips audit log.
    Each row is anchored to a unique block_history_id and is preserved indefinitely for
    compliance review.
  </div>
</body></html>`);
  win.document.close();
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, c => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' }[c]!));
}
